import {
  IonContent,
  IonPage,
  isPlatform,
  useIonToast,
  IonSpinner,
} from "@ionic/react";
import React from "react";
import { IconMessageCircle, IconPhoneCall } from "@tabler/icons-react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet";

import config from "../../config";

// API
import WebChatApi from "../../api/web-chat";

// Store
import { useWebChatActions, useWebChatData } from "../../store/web-chat";
import useContactsStore from "../../store/contacts";

// Interfaces
import { ChatInputRef, Message } from "../../interfaces";

// Components
import ChatDetail from "../chat-detail";
import ChatInput from "../common/chat-input";
import FileInput from "../common/file-input";
import Call from "../common/call";
import Modal from "./modal";
import UploadPhotoModal from "../UploadPhotoModal";

// socket
import { useSockets } from "../../providers/socket";
import { useSocketStore } from "../../store/socket";

// Styles
import webChatStyles from "./styles.module.css";

// Utils
import { useTabActive } from "../../utils/useActiveTab";
import { useSelectedContactData } from "../../hooks";

const isMobile = isPlatform("android") || isPlatform("ios");
const version = config.VERSION;

function checkPlan(plan?: string | null) {
  if (!plan) return false;
  const sanitizePlan = plan.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  const lower = sanitizePlan.toLowerCase();
  return lower.includes("pro");
}

function WebChat() {
  // Get id from URL
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const id = params.get("id");

  //Active tab
  const isTabActive = useTabActive();
  const isInIframe = window.self !== window.top;

  const { webChatSocket, emitEventWebChat } = useSockets();
  const { setWebChatQuery, socketReady, socketSessionReady } = useSocketStore();
  const { updateContactsDb } = useContactsStore();

  const [name, setName] = React.useState<string>("");
  const [webChatReady, setWebChatReady] = React.useState(false);
  const [chatOpen, setChatOpen] = React.useState(false);
  const [tabOpen, setTabOpen] = React.useState(true);
  const [scroll, setScroll] = React.useState(0);
  const [content, setContent] = React.useState<string>();
  const [isFocus, setIsFocus] = React.useState(0);
  const [typingIndicator, setTypingIndicator] = React.useState(false);
  const [replyTo, setReplyTo] = React.useState<Message | null>(null);
  const [channelMode, setChannelMode] = React.useState<
    "menu" | "message" | "call"
  >("menu");

  const [lastId, setLastId] = React.useState<string>("");
  const [unreadCount, setUnreadCount] = React.useState<number>(0);
  const chatInputRef = React.useRef<ChatInputRef>(null);

  const hiddenFileInput = React.useRef<HTMLInputElement | null>(null);
  const hiddenDocInput = React.useRef<HTMLInputElement | null>(null);
  const [isOpenUpload, setIsOpenUpload] = React.useState(false);
  const [isLoadingUpload, setIsLoadingUpload] = React.useState(false);
  const [files, setFiles] = React.useState<File[]>([]);
  const [docFiles, setDocFiles] = React.useState<File[]>([]);
  const [isOpenDocsUpload, setIsDocsOpenUpload] =
    React.useState<boolean>(false);

  const intl = useIntl();

  const { createWebId, activateWebId, deActivateWebId } = useWebChatActions();

  const { webChat } = useWebChatData(id);
  const { messages } = useSelectedContactData(webChat?.webId || "");
  const [present] = useIonToast();
  const existsId = id !== null && id.length > 0;

  const {
    data: companyData,
    isLoading: isGettingCompany,
    isError: isGettingCompanyError,
  } = useQuery({
    queryFn: () => WebChatApi.getCompanyInfo({ id: String(id) }),
    queryKey: ["getCompanyInfo", id],
    enabled: existsId,
    refetchOnWindowFocus: false,
  });

  const {
    data: sendResponse,
    mutate: mutateSendMessage,
    isPending: isSendingMessage,
  } = useMutation({
    mutationFn: WebChatApi.sendMessage,
    onSuccess: (data) => {
      if (!data.ok) {
        return present({
          message: intl.formatMessage({ id: "common.tryItLater" }),
          duration: 6000,
          color: "light",
          position: "top",
        });
      }
    },
    onError: () => {
      return present({
        message: intl.formatMessage({ id: "common.tryItLater" }),
        duration: 6000,
        color: "light",
        position: "top",
      });
    },
  });

  // Post Read Messages
  const { mutate: mutateReadMessages } = useMutation({
    mutationFn: WebChatApi.readMessages,
  });

  // Post Delivered Messages
  const { mutate: mutateDeliveredMessages } = useMutation({
    mutationFn: WebChatApi.deliveredMessages,
  });

  const { mutateAsync: mutateCreateContact, isPending: isCreatingContact } =
    useMutation({
      mutationFn: WebChatApi.createContact,
      onError: () => {
        return present({
          message: intl.formatMessage({ id: "common.tryItLater" }),
          duration: 6000,
          color: "light",
          position: "top",
        });
      },
    });

  const onChangeContent = (newContent: string) => setContent(newContent);

  const onSubmit = () => {
    if (content && webChat?.companyId && webChat?.webId) {
      mutateSendMessage({
        webId: webChat?.webId,
        content,
        replyTo: replyTo?.id,
        companyId: webChat.companyId,
        name,
      });
    }
  };

  const onReply = (message: Message, content: string) => {
    if (content && webChat?.webId && webChat?.companyId) {
      mutateSendMessage({
        webId: webChat.webId,
        content,
        replyTo: message ? message.id : undefined,
        companyId: webChat.companyId,
        name,
      });
    }
  };

  const sendPhotos = async (content: FormData) => {
    if (webChat?.webId) {
      setIsLoadingUpload(true);
      const { err } = await WebChatApi.uploadMedia(webChat.webId, content);
      setIsLoadingUpload(false);
      if (err) {
        if (err.error) {
          return present({
            message: err.error?.[intl.locale],
            duration: 6000,
            color: "warning",
            position: "bottom",
          });
        }
      }
      setIsOpenUpload(false);
      return onHandleModalClose();
    }
  };

  const onHandleModalClose = () => {
    setIsDocsOpenUpload(() => false);
    setIsOpenUpload(() => false);

    if (hiddenFileInput?.current?.value) {
      hiddenFileInput.current.value = "";
    }
    if (hiddenDocInput.current?.value) {
      hiddenDocInput.current.value = "";
    }

    setFiles([]);
    return setDocFiles([]);
  };

  const onChangeFileInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "photo" | "doc",
  ) => {
    setIsLoadingUpload(false);
    const fileList = e.target.files;
    if (fileList && fileList.length > 0) {
      const files = [...fileList];

      if (type === "photo") {
        setFiles(files);
        return setIsOpenUpload(true);
      }

      setDocFiles(files);
      return setIsDocsOpenUpload(true);
    }
  };

  const openSendPhoto = () => {
    hiddenFileInput.current?.click();
  };

  const openSendDoc = () => {
    hiddenDocInput.current?.click();
  };

  const ensureWebChatSession = async () => {
    if (!webChat?.webId || !webChat?.companyId) return false;
    if (!webChat?.localOnly) return true;
    if (isCreatingContact) return false;
    const res = await mutateCreateContact({
      webId: webChat.webId,
      companyId: webChat.companyId,
      name: name || "Webchat user",
    });
    if (res?.data?.ok) {
      activateWebId(webChat.companyId, webChat.webId);
      return true;
    }
    if (res?.err) {
      present({
        message: intl.formatMessage({ id: "common.tryItLater" }),
        duration: 6000,
        color: "light",
        position: "top",
      });
    }
    return false;
  };

  const handleSelectCall = async () => {
    if (id !== "cd309e45c8ef6980b3bacbc7022e228f") return;
    if (isCreatingContact) return;
    setChannelMode("call");
    await ensureWebChatSession();
  };

  const handleSelectMessage = () => {
    setChannelMode("message");
  };

  React.useEffect(() => {
    if (companyData?.ok && id) {
      createWebId(id);
    }
  }, [id, companyData]);

  React.useEffect(() => {
    if (messages.length > 0 && channelMode === "menu") {
      setChannelMode("message");
    }
  }, [messages.length, channelMode]);

  React.useEffect(() => {
    if (
      id &&
      id !== "cd309e45c8ef6980b3bacbc7022e228f" &&
      channelMode === "menu"
    ) {
      setChannelMode("message");
    }
  }, [id, channelMode]);

  React.useEffect(() => {
    if (channelMode === "call" && id !== "cd309e45c8ef6980b3bacbc7022e228f") {
      setChannelMode("message");
    }
  }, [channelMode, id]);

  React.useEffect(() => {
    if (sendResponse && sendResponse?.ok) {
      setContent("");
      setReplyTo(null);
    }
  }, [sendResponse]);

  React.useEffect(() => {
    if (sendResponse && sendResponse?.ok && !webChatReady) {
      activateWebId(sendResponse.companyId, sendResponse.webId);
    }
  }, [sendResponse, webChatReady]);

  // If we have a webId, connect to socket
  React.useEffect(() => {
    if (webChat?.webId && !webChat?.localOnly) {
      const query = {
        webChatId: webChat.webId,
        ...version,
        mobile: isMobile,
      };
      // Connect to socket
      setWebChatQuery(query);
    }
  }, [webChat?.webId, webChat?.localOnly]);

  React.useEffect(() => {
    if (!webChatSocket) return;
    if (!socketSessionReady) return;
    const handleNewMessage = (message: any) => {
      const { data } = message;
      if (data) {
        const dataArray = Array.isArray(data.data) ? data.data : [];
        updateContactsDb(dataArray, { isWebChat: true });
        if (data.newMessage) {
          const lastId = dataArray[dataArray.length - 1]?._id;
          const unreadCount = dataArray.reduce(
            (count: number, message: any) => {
              return message.status !== "read" && message.role === "assistant"
                ? count + 1
                : count;
            },
            0,
          );
          setLastId(lastId || "");
          setUnreadCount(unreadCount);
          setTypingIndicator(false);
        }
      }
    };
    const handleChatSession = (message: any) => {
      if (!message?.data.success) {
        setWebChatReady(false);
        if (id && webChat?.webId) {
          deActivateWebId(id, webChat.webId);
        }
      }

      if (message?.data.success) {
        //console.log("webchat ready to use....");
        emitEventWebChat({ name: "initial-connection", data: {} });
        setScroll(scroll + 1);
        setWebChatReady(true);
      }
    };

    const handleIndicator = (message: any) => {
      const { data } = message;
      setTypingIndicator(data?.typing || false);
    };

    focusChatInput();

    // Attach event listener
    webChatSocket.on("new-message", handleNewMessage);
    webChatSocket.on("chat-session", handleChatSession);
    webChatSocket.on("typing", handleIndicator);

    emitEventWebChat({
      name: "chat-session",
      data: { webChatId: webChat?.webId },
    });

    return () => {
      console.log("cleaning up handler chats...");
      webChatSocket.off("new-message", handleNewMessage);
      webChatSocket.off("chat-session", handleChatSession);
      webChatSocket.off("typing", handleIndicator);
    };
  }, [webChatSocket, socketSessionReady]);

  React.useEffect(() => {
    if (!isInIframe) {
      setTabOpen(isTabActive);
    }
  }, [isTabActive]);

  React.useEffect(() => {
    if (!isInIframe) return;
    window.parent.postMessage(
      {
        event: "chat:unreadCount",
        count: unreadCount,
      },
      "*",
    );
  }, [unreadCount]); // Whenever unread count changes

  React.useEffect(() => {
    const handleParentEvent = (e: MessageEvent) => {
      const data = e.data;
      switch (e.data?.event) {
        case "chat:opened":
          setChatOpen(true);
          setScroll(scroll + 1);
          break;
        case "chat:closed":
          setChatOpen(false);
          break;
        case "tab:visibility":
          setTabOpen(data.isActive);
          break;
        default:
          break;
      }
    };
    window.addEventListener("message", handleParentEvent);
    return () => window.removeEventListener("message", handleParentEvent);
  }, []);

  React.useEffect(() => {
    if (webChat?.webId && webChat?.companyId && lastId) {
      isInIframe && chatOpen && tabOpen
        ? mutateReadMessages({
            webId: webChat?.webId,
            companyId: webChat?.companyId,
            lastId,
          })
        : !isInIframe && tabOpen
        ? mutateReadMessages({
            webId: webChat?.webId,
            companyId: webChat?.companyId,
            lastId,
          })
        : mutateDeliveredMessages({
            webId: webChat?.webId,
            companyId: webChat?.companyId,
            lastId,
          });
    }
  }, [chatOpen, tabOpen, lastId]);

  const inputFocused = () => setTimeout(() => setIsFocus(isFocus + 1), 650);

  const focusChatInput = () => chatInputRef.current?.setInputFocus();

  const companyName = companyData?.company?.name || "";
  const isError = isGettingCompanyError || !existsId;
  const isProPlan = checkPlan(companyData?.company?.plan);
  const callReady =
    Boolean(webChat?.webId) && !webChat?.localOnly && socketSessionReady;
  const showGate =
    channelMode === "menu" && id === "cd309e45c8ef6980b3bacbc7022e228f";
  const showCall = channelMode === "call";

  const title = `Visito | ${companyName} web chat`;
  const description = `Chat with ${companyName} using Visito's AI virtual assistant. Ask questions, get answers, and more.`;
  const image =
    "https://cdn.prod.website-files.com/659ea70342c43623b694723a/65cf09976f795389644fd344_visito%20thumbnailV3.png";

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content="" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={image} />
        <meta
          property="twitter:title"
          content={`Visito | ${companyName || ""} Playground`}
        />
        <meta property="twitter:description" content={description} />
        <meta property="twitter:image" content={image} />
      </Helmet>
      <IonPage id="main-content">
        <IonContent
          {...(isMobile
            ? { fullscreen: true, color: "light", className: "ion-padding" }
            : {})}
        >
          <div className={isMobile ? "view-wrapper h-100" : "page-wrapper"}>
            {isError ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100vh",
                }}
              >
                <div className="fs-4">
                  {" "}
                  There was an error. If you are the owner of the website,
                  contact support.
                </div>
              </div>
            ) : (
              <div className={webChatStyles.webChatWrapper}>
                <div className={webChatStyles.webChatDetailHeader}>
                  <div>
                    <h3 className="fs-4 mb-0">
                      {!socketReady && !webChat?.localOnly ? (
                        <div className="connection-status fs-1 color-step-700 flex align-items-center ">
                          <IonSpinner
                            color="medium"
                            name="lines-sharp-small"
                          ></IonSpinner>{" "}
                          <FormattedMessage id="common.connecting" />
                        </div>
                      ) : (
                        <FormattedMessage
                          id="aiCompanyTitle"
                          values={{ company: companyName }}
                        />
                      )}
                    </h3>
                  </div>
                </div>
                <div className={webChatStyles.chatDetailContainer}>
                  <div className={webChatStyles.chatDetailBody}>
                    {showGate ? (
                      <div className={webChatStyles.channelGate}>
                        <div className={webChatStyles.channelGateCard}>
                          <div className={webChatStyles.channelGateTitle}>
                            <FormattedMessage
                              id="webchat.welcome"
                              values={{
                                linebreak: <br />,
                              }}
                            />
                          </div>
                          <div className={webChatStyles.channelGateActions}>
                            <button
                              className={`btn btn-visito-light ${webChatStyles.channelChoice}`}
                              onClick={handleSelectMessage}
                              type="button"
                            >
                              <span
                                className={webChatStyles.channelChoiceLabel}
                              >
                                <FormattedMessage id="common.chat" />
                              </span>
                            </button>
                            {id === "cd309e45c8ef6980b3bacbc7022e228f" && (
                              <button
                                className={`btn btn-visito-light ${webChatStyles.channelChoice}`}
                                onClick={handleSelectCall}
                                disabled={isCreatingContact}
                                type="button"
                              >
                                <span
                                  className={webChatStyles.channelChoiceLabel}
                                >
                                  <FormattedMessage id="common.call" />
                                </span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className={webChatStyles.channelSwitcher}>
                          <div className="nav">
                            <div className="nav-item">
                              <a
                                role="button"
                                className={`nav-link ${
                                  channelMode === "message" ? "active" : ""
                                }`}
                                tabIndex={0}
                                onClick={handleSelectMessage}
                              >
                                <div className="d-flex align-items-center gap-2">
                                  <IconMessageCircle size={16} />
                                  <FormattedMessage id="common.chat" />
                                </div>
                              </a>
                            </div>
                            {id === "cd309e45c8ef6980b3bacbc7022e228f" && (
                              <div className="nav-item">
                                <a
                                  role="button"
                                  className={`nav-link ${
                                    channelMode === "call" ? "active" : ""
                                  }`}
                                  tabIndex={0}
                                  onClick={handleSelectCall}
                                  aria-disabled={isCreatingContact}
                                >
                                  <div className="d-flex align-items-center gap-2">
                                    <IconPhoneCall size={16} />
                                    <FormattedMessage id="common.call" />
                                  </div>
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className={webChatStyles.channelPanel}>
                          {showCall &&
                          id === "cd309e45c8ef6980b3bacbc7022e228f" ? (
                            <div className={webChatStyles.callPanel}>
                              {callReady ? (
                                <Call
                                  displayName={companyName || "Visito AI"}
                                  className={`${webChatStyles.callButton} btn btn-visito-primary btn-xs btn-flex`}
                                  icon={
                                    <>
                                      <IconPhoneCall size={16} />
                                      <FormattedMessage
                                        id="webchat.callPrompt"
                                        values={{
                                          company: companyName || "our team",
                                        }}
                                      />
                                    </>
                                  }
                                  onCall={() => console.log("calling")}
                                  onHangUp={() => console.log("hangup")}
                                  contactId={webChat?.webId || undefined}
                                  companyId={webChat?.companyId || undefined}
                                  type={"web-chat"}
                                  socketType="web-chat"
                                />
                              ) : (
                                <button
                                  className="btn btn-visito-light btn-xs btn-flex"
                                  disabled
                                >
                                  <IonSpinner
                                    color="medium"
                                    name="lines-sharp-small"
                                  />
                                  <FormattedMessage id="common.connecting" />
                                </button>
                              )}
                            </div>
                          ) : !webChatReady && webChat?.localOnly ? (
                            <div className={webChatStyles.webChatWelcome}>
                              <div>
                                <FormattedMessage
                                  id="webchat.welcome"
                                  values={{
                                    linebreak: <br />,
                                  }}
                                />
                              </div>
                            </div>
                          ) : (
                            <ChatDetail
                              mode={isMobile ? "ios" : "desktop"}
                              scrollEscalations={1}
                              scroll={scroll}
                              type="web-chat"
                              replyTo={({ message }: { message: Message }) => {
                                setReplyTo(message);
                                inputFocused();
                              }}
                              typingIndicator={typingIndicator}
                              selectedId={webChat?.webId || ""}
                              actionClick={({
                                message,
                                text,
                              }: {
                                message: Message;
                                text: string;
                              }) => onReply(message, text)}
                            />
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {!showGate && !showCall && (
                  <div className={`${webChatStyles.chatInputContainer}`}>
                    <FileInput
                      ref={hiddenFileInput}
                      accept="image/png, image/jpeg, image/webp"
                      multiple
                      onChange={(e) => onChangeFileInput(e, "photo")}
                      style={{ display: "none" }}
                    />
                    <FileInput
                      ref={hiddenDocInput}
                      accept="application/pdf, .pdf"
                      onChange={(e) => onChangeFileInput(e, "doc")}
                      style={{ display: "none" }}
                    />
                    <ChatInput
                      id="send-body-playground"
                      ref={chatInputRef}
                      value={content}
                      placeholder={intl.formatMessage({
                        id: "chats.newMessageInput",
                      })}
                      channel="web"
                      onOpenPhotoInput={openSendPhoto}
                      onOpenDocInput={openSendDoc}
                      onInputChange={onChangeContent}
                      onBeforeSubmit={onSubmit}
                      onAfterFocus={() =>
                        isMobile ? inputFocused() : undefined
                      }
                      replyTo={replyTo}
                      cleanReplyTo={() => setReplyTo(null)}
                    />
                  </div>
                )}
                {!isProPlan && (
                  <div className={webChatStyles.webChatDetailFooter}>
                    <p className="mb-0">
                      <FormattedMessage id="aiPoweredBy" />{" "}
                      <a
                        className="mb-0"
                        href="https://visitoai.com"
                        target="_blank"
                      >
                        Visito AI
                      </a>
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          {!webChatReady && webChat?.localOnly && !name && (
            <Modal loading={isGettingCompany} onSubmit={setName} />
          )}
        </IonContent>
        <UploadPhotoModal
          show={isOpenUpload || isOpenDocsUpload}
          sending={isLoadingUpload}
          handleClose={onHandleModalClose}
          files={isOpenDocsUpload ? docFiles : files}
          upload={sendPhotos}
          mode={`mobile`}
          areDocs={isOpenDocsUpload}
          channel={"web"}
        />
      </IonPage>
    </>
  );
}

export default WebChat;
